import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  ListGroup,
  Badge,
  Alert,
} from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast'; // Import toast

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

function CollaborationRequests({ token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRequests = async () => {
    if (!token) {
      setError(
        'Authentication required. Please provide a GitHub token.'
      );
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching collaboration requests...');
      const response = await axios.get(
        `${BACKEND_URL}/api/collaboration-requests`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      console.log('Collaboration requests response:', response.data);
      setRequests(response.data.requests || []);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching collaboration requests:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });

      let errorMessage = 'Failed to fetch collaboration requests';

      if (err.response?.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }

        // Add permission requirements if present
        if (err.response.data.required_permissions) {
          errorMessage +=
            '\n\nRequired GitHub token permissions:\n' +
            err.response.data.required_permissions
              .map((p) => `• ${p}`)
              .join('\n');
        }
      } else if (!err.response) {
        errorMessage =
          'Unable to connect to the server. Please check your network connection.';
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleAccept = async (invitationId) => {
    try {
      console.log('Accepting invitation:', invitationId);
      const response = await axios.post(
        `${BACKEND_URL}/api/collaboration-requests/${invitationId}/accept`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('Accept response:', response.data);
      // Remove the accepted invitation from the list
      setRequests(requests.filter((req) => req.id !== invitationId));
      // Show success message
      toast.success('Invitation accepted successfully');
    } catch (err) {
      console.error('Error accepting invitation:', err.response || err);
      const errorMessage = err.response?.data?.message || 'Failed to accept invitation. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleReject = async (invitationId) => {
    try {
      console.log('Rejecting invitation:', invitationId);
      const response = await axios.post(
        `${BACKEND_URL}/api/collaboration-requests/${invitationId}/reject`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      console.log('Reject response:', response.data);
      // Remove the rejected invitation from the list
      setRequests(requests.filter((req) => req.id !== invitationId));
      // Show success message
      toast.success('Invitation rejected successfully');
    } catch (err) {
      console.error('Error rejecting invitation:', err.response || err);
      const errorMessage = err.response?.data?.message || 'Failed to reject invitation. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (token) {
      fetchRequests();
      // Set up polling every 30 seconds
      const interval = setInterval(fetchRequests, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">Collaboration Requests</h5>
        </Card.Header>
        <Card.Body>
          <div>Loading collaboration requests...</div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Collaboration Requests</h5>
        {requests.length > 0 && (
          <Badge bg="primary" pill>
            {requests.length}
          </Badge>
        )}
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" className="white-space-pre-wrap">
            {error}
          </Alert>
        )}
        {requests.length > 0 ? (
          <ListGroup>
            {requests.map((request) => (
              <ListGroup.Item key={request.id}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6>
                      <a
                        href={request.repository.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {request.repository.full_name}
                      </a>
                    </h6>
                    <small className="text-muted">
                      Invited by{' '}
                      <a
                        href={request.sender.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {request.sender.login}
                      </a>{' '}
                      with {request.permission} permissions
                    </small>
                  </div>
                  <div>
                    <Button
                      variant="success"
                      size="sm"
                      className="me-2"
                      onClick={() => handleAccept(request.id)}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleReject(request.id)}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <p className="text-center">No pending collaboration requests</p>
        )}
      </Card.Body>
    </Card>
  );
}

export default CollaborationRequests;
